import React, { Dispatch, ReactElement, SetStateAction } from 'react'
import { Flex as CkFlex, Box, AspectRatio, Img, HStack } from '@chakra-ui/react'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { EBreakPoint } from 'enums/theme'
import useBreakPoint from 'hooks/useBreakPoint'
import isEmpty from 'lodash/isEmpty'
import Icon from 'components/Icon'
import { defaultSlide } from 'components/MultiCarousel/constants'
import { ICarouseItem } from 'interfaces/listing'
import { IMedia } from 'interfaces/media'
import { IRoomDefaultValue } from 'interfaces/roomDefaultValue'
import { IUnitWithRelations } from 'interfaces/unit'
import { getDefaultSlide, getValidArray } from 'utils/common'
import {
  RoomCardBioContainer,
  RoomCardBioFooter,
  RoomCardBioText,
  RoomCardBioTitle,
  RoomCardContainer,
  RoomCardWrapper,
  Flex,
  RoomInfoRowMobile,
  RoomCardViewHouseButton,
  RoomCardRadioButton,
  RoomCardViewHouseMobileButton
} from './roomCard.styles'
import { getAvailableDateInRoomCard, getImageMediaUnit } from './utils'
dayjs.extend(advancedFormat)

export interface IRoomCardProps
  extends Pick<
    IUnitWithRelations,
    'price' | 'name' | 'id' | 'availableStartDate' | 'availableEndDate' | 'buildiumUnitId' | 'media'
  > {
  availableDate?: Date
  defaultRoomValue: IRoomDefaultValue
  isFullWidth?: boolean
  houseMedia: IMedia[]
  isChosen?: boolean
  setChosenUnitId: Dispatch<SetStateAction<number>>
  vrUrl?: string
}

const RoomCard = (props: IRoomCardProps): ReactElement => {
  const {
    price,
    name,
    defaultRoomValue,
    availableDate,
    buildiumUnitId,
    isFullWidth = false,
    media,
    isChosen,
    setChosenUnitId,
    vrUrl
  } = props

  const isMobile: boolean = useBreakPoint(EBreakPoint.BASE, EBreakPoint.MD)
  const roomImages: ICarouseItem[] = getImageMediaUnit(media)
  const validRoomImages: ICarouseItem[] = !isEmpty(roomImages)
    ? getValidArray(roomImages)
    : getDefaultSlide(defaultSlide, defaultRoomValue?.roomDefaultImage ?? defaultSlide[0]?.url)
  const mainRoomImage: ICarouseItem = validRoomImages[0]
  const validVrUrl: string = vrUrl?.includes('http') ? vrUrl : ''

  const availableDateInRoomCard: string = getAvailableDateInRoomCard(defaultRoomValue, availableDate)

  function handleChooseRoom(): void {
    buildiumUnitId && setChosenUnitId(buildiumUnitId)
  }

  function handleShow3DRoom(event: React.MouseEvent<HTMLDivElement, MouseEvent>): void {
    if (event?.stopPropagation) event.stopPropagation()
    if (validVrUrl) {
      window.open(validVrUrl, '_blank')
    }
  }

  return (
    <RoomCardWrapper>
      <RoomCardContainer isChosen={isChosen}>
        <AspectRatio ratio={16 / 9} height={{ base: '159px', md: '198px', lg: '136px' }}>
          <Img src={mainRoomImage.url} alt={mainRoomImage.name} onClick={handleChooseRoom} cursor="pointer" />
        </AspectRatio>

        <RoomCardRadioButton isChosen={isChosen} onClick={handleChooseRoom} />
        <RoomCardBioContainer>
          <HStack justifyContent="space-between" alignItems="flex-start">
            <RoomCardBioTitle noMargin blackColor={true} onClick={handleChooseRoom}>
              {name}
            </RoomCardBioTitle>
            {!isMobile && (
              <RoomCardViewHouseButton onClick={handleShow3DRoom} isDisabled={!validVrUrl}>
                3D Tour
              </RoomCardViewHouseButton>
            )}
          </HStack>
          <RoomCardBioFooter onClick={handleChooseRoom}>
            <Flex gap={2}>
              {isMobile && !isFullWidth ? (
                <RoomInfoRowMobile>
                  <Box width={4} height={4} paddingTop="1px !important">
                    <Icon iconName="circle_check_gray.svg" size={16} />
                  </Box>
                  <RoomCardBioText bold>{`Avail: ${availableDateInRoomCard}`}</RoomCardBioText>
                </RoomInfoRowMobile>
              ) : (
                <CkFlex>
                  <Box width={4} height={4} paddingTop="1px !important">
                    <Icon iconName="circle_check_gray.svg" size={16} />
                  </Box>
                  <RoomCardBioText bold>{`Avail: ${availableDateInRoomCard}`}</RoomCardBioText>
                </CkFlex>
              )}

              <CkFlex>
                <Icon iconName="price_tag.svg" size={16} />
                <RoomCardBioText price bold>
                  ${price}+/mo
                </RoomCardBioText>
              </CkFlex>
            </Flex>
            {isMobile && (
              <RoomCardViewHouseMobileButton onClick={handleShow3DRoom} isDisabled={!validVrUrl}>
                3D Tour
              </RoomCardViewHouseMobileButton>
            )}
          </RoomCardBioFooter>
        </RoomCardBioContainer>
      </RoomCardContainer>
    </RoomCardWrapper>
  )
}

export default RoomCard
